import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { isClock } from '@/shared/lib/calendar';

import styles from './DateTimeFields.module.css';

const PRESETS = ['09:00', '12:00', '15:00', '18:00'] as const;

interface Props {
  label: string;
  /** `HH:MM`, or empty for "no time". */
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  describedBy?: string;
}

/** Normalizes typing into `HH:MM`: digits only, the colon is inserted after the hour. */
function maskClock(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  return digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
}

/** Optional 24-hour time with quick presets and a clear button. */
export function TimeField({ label, value, onChange, invalid = false, describedBy }: Props) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.label}>
        {label}
      </label>
      <span className={styles.timeRow}>
        <input
          id={id}
          className={`${styles.control} ${styles.time}`}
          inputMode="numeric"
          autoComplete="off"
          placeholder={t('calendar.timePlaceholder')}
          maxLength={5}
          value={value}
          aria-invalid={invalid || (value !== '' && !isClock(value))}
          aria-describedby={describedBy}
          onChange={(event) => {
            onChange(maskClock(event.target.value));
          }}
        />
        {value && (
          <button
            type="button"
            className={styles.clear}
            aria-label={t('calendar.clearTime', { label })}
            title={t('calendar.clearTime', { label })}
            onClick={() => {
              onChange('');
            }}
          >
            ×
          </button>
        )}
      </span>
      {!value && (
        <span className={styles.presets}>
          {PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              className={styles.preset}
              aria-label={`${label} ${preset}`}
              onClick={() => {
                onChange(preset);
              }}
            >
              {preset}
            </button>
          ))}
        </span>
      )}
    </div>
  );
}
